import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { formatIngreso } from "@/lib/leadPeriod";

const EMPTY = { nombre: "", correo: "", telefono: "" };

export function AgencyAdvisors({ agencyId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function load() {
    try { const { data } = await api.get(`/admin/advisors?agency_id=${agencyId}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, [agencyId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function create(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/agencies/${agencyId}/advisors`, { ...form, activo: true });
      toast[data.invite_sent ? "success" : "warning"](data.invite_sent ? `Invitación enviada a ${data.correo}` : "Asesor creado, pero el correo no se pudo enviar. Usa 'Reenviar invitación'.");
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  async function toggle(a) {
    if (a.activo && !window.confirm(`¿Desactivar a ${a.nombre}? Perderá el acceso de inmediato y dejará de recibir leads.`)) return;
    try { await api.patch(`/admin/advisors/${a.id}`, { activo: !a.activo }); toast.success(a.activo ? "Asesor desactivado" : "Asesor activado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function resend(a) {
    try { const { data } = await api.post(`/admin/advisors/${a.id}/resend-invite`); toast[data.ok ? "success" : "error"](data.ok ? `Invitación reenviada a ${a.correo}` : "No se pudo enviar el correo"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="rounded-sm border border-[#3E5641]/10 p-3" data-testid="agency-advisors">
      <p className="text-sm font-medium text-[#3E5641]">Asesores</p>
      <ul className="mt-2 divide-y divide-[#3E5641]/10">
        {items.length === 0 && <li className="py-2 text-xs text-[#6B7678]">Sin asesores aún.</li>}
        {items.map((a) => (
          <li key={a.id} className="flex items-center gap-3 py-2" data-testid={`advisor-item-${a.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-[#3E5641]">{a.nombre} {!a.activo && <span className="ml-1 rounded-sm bg-red-50 px-1 text-[10px] text-red-700">Inactivo</span>}</p>
              <p className="truncate text-xs text-[#6B7678]">{a.correo} · {a.telefono}</p>
              <p className="text-[10px] text-[#6B7678]" data-testid={`advisor-status-${a.id}`}>
                {a.password_set ? "Contraseña definida" : a.invite_failed_at && (!a.invite_sent_at || a.invite_failed_at > a.invite_sent_at)
                  ? `No se pudo enviar la invitación (${formatIngreso(a.invite_failed_at)}) · reenvíala`
                  : `Invitación pendiente${a.invite_sent_at ? ` · enviada ${formatIngreso(a.invite_sent_at)}` : ""}`}
              </p>
            </div>
            {!a.password_set && a.activo && (
              <button type="button" onClick={() => resend(a)} className="p-1 text-[#A0553F] hover:bg-[#F1F3F3]" title="Reenviar invitación" data-testid={`advisor-resend-${a.id}`}><Mail size={14} /></button>
            )}
            <Switch checked={a.activo} onCheckedChange={() => toggle(a)} data-testid={`advisor-active-${a.id}`} />
          </li>
        ))}
      </ul>
      <form onSubmit={create} className="mt-3 grid gap-2 border-t border-[#3E5641]/10 pt-3" data-testid="advisor-new-form">
        <Input required placeholder="Nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="advisor-new-nombre" />
        <Input required type="email" placeholder="Correo (será su usuario)" value={form.correo} onChange={(e) => setForm({ ...form, correo: e.target.value })} data-testid="advisor-new-correo" />
        <Input required placeholder="Teléfono / WhatsApp" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="advisor-new-telefono" />
        <button type="submit" disabled={saving} className="btn-outline-dark disabled:opacity-60" data-testid="advisor-new-submit">
          <Plus size={14} className="mr-1 inline" /> {saving ? "Dando de alta..." : "Dar de alta y enviar invitación"}
        </button>
      </form>
    </div>
  );
}
